/**
 * Returns display utilities: center returns at 0 (multiplier 1.0 = break-even).
 * Raw data is assumed to be multiplier form (1.0 = break-even, 1.24 = 124% total return).
 * Display: centered = (raw - 1) * 100 → "+24.00%" or "-5.00%"
 */

/**
 * Convert raw return (multiplier) to centered percentage number for comparison/sort/charts.
 * @param {number|null|undefined} raw - Multiplier (1.0 = break-even, 1.24 = 124%)
 * @returns {number|null} Centered value (e.g. 24, -5) or null if invalid
 */
export function returnsToCentered(raw) {
  if (raw === undefined || raw === null || typeof raw !== 'number' || Number.isNaN(raw)) {
    return null;
  }
  return (raw - 1) * 100;
}

/**
 * Format raw return (multiplier) as centered percentage string with sign.
 * @param {number|null|undefined} raw - Multiplier (1.0 = break-even, 1.24 = 124%)
 * @returns {string} e.g. "+24.00%", "-5.00%", "0.00%", or "N/A"
 */
export function formatReturnsCentered(raw) {
  const centered = returnsToCentered(raw);
  if (centered === null) return 'N/A';
  const sign = centered > 0 ? '+' : '';
  return `${sign}${centered.toFixed(2)}%`;
}

/**
 * The custom builder's backtest estimate (portfolio_returns on a custom playbook): a plain
 * fraction of the stake per year (0.024 = +2.4%), NOT a multiplier like the Odikai playbooks'
 * returns. Reading it as a multiplier turns +2.4% into -97.6%.
 * @param {number|null|undefined} fraction
 * @returns {string} e.g. "+2.41%", "-0.85%", or "N/A"
 */
export function formatExpectedReturn(fraction) {
  if (typeof fraction !== 'number' || Number.isNaN(fraction)) return 'N/A';
  const pct = fraction * 100;
  return `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`;
}

/** 'positive', 'negative' or '' (no number) for a value centred at 0. */
export function toneOfSigned(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return value >= 0 ? 'positive' : 'negative';
}

/** A dollar change with its sign: "+$812", "-$40", or "N/A". */
export function formatDollarChange(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return 'N/A';
  const amount = Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return `${value < 0 ? '-' : '+'}$${amount}`;
}
